import { inject, Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { EventType } from "@azure/msal-browser";
import { KlopCustomer } from "@kpx/core";
import { CollectionSubject } from "@kpx/helpers";
import { HttpService } from "@kpx/http";
import { MachineManagementConfig, MachineManagementService } from "@kpx/machine-management";
import { GetConfigResponse, MaintenanceService, QuickscanService } from "@kpx/quickscan";
import { BehaviorSubject, filter, firstValueFrom, pairwise, skip } from "rxjs";
import { ENVIRONMENT } from "../../environments/environment";
import { SessionPermission } from "../../types/session-permission";
import { List } from "../../types/types";
import { HealthCheckResponse, User, UserGroup, UserInfoResponse } from "../../types/user";
import { API_ROUTES } from "../api.config";
import { PERMISSIONS } from "../config/permissions";
import { ROLES } from "../config/roles";
import { STORAGE } from "../config/storage";
import { Misc } from "../helpers/misc";
import { ApplicationService } from "./application.service";

export interface TenantProfile {
  tenantId: string;
  localAccountId: string;
  name: string;
  isHomeTenant: boolean;
}

export interface AquireTokenSuccessPayload {
  authority: string;
  uniqueId: string;
  tenantId: string;
  scopes: string[];
  account: {
    homeAccountId: string;
    environment: string;
    tenantId: string;
    username: string;
    localAccountId: string;
    name: string;
    authorityType: string;
    tenantProfiles: Map<string, TenantProfile>;
    idTokenClaims: {
      aud: string;
      iss: string;
      iat: number;
      nbf: number;
      exp: number;
      idp: string;
      name: string;
      nonce: string;
      oid: string;
      preferred_username: string;
      rh: string;
      sub: string;
      tid: string;
      uti: string;
      ver: string;
    };
    idToken: string;
  };
  idToken: string;
  idTokenClaims: {
    aud: string;
    iss: string;
    iat: number;
    nbf: number;
    exp: number;
    idp: string;
    name: string;
    nonce: string;
    oid: string;
    preferred_username: string;
    rh: string;
    sub: string;
    tid: string;
    uti: string;
    ver: string;
  };
  accessToken: string;
  fromCache: true;
  expiresOn: string;
  extExpiresOn: string;
  correlationId: string;
  requestId: string;
  familyId: string;
  tokenType: string;
  state: string;
  cloudGraphHostName: string;
  msGraphHost: string;
  fromNativeBroker: false;
}

export enum SessionStatus {
  DEFAULT,
  VALID,
  IN_PROGRESS,
  UNAVAILABLE,
  INVALID,
}

export interface SessionRole {
  title: string;
  code: string;
}

export interface Customer {
  id: string;
  Customer_number: string;
  Customer_name: string;
  Customer_code: string;
  Logo_url: string;
  Region_id: string;
  Department_id: string;
  Description: string;
  VAT_number: string;
  Purchaseorders_present: string;
  Billing_category: number;
  CMO_present: string;
  Country_code: string;
}

@Injectable({ providedIn: "root" })
export class SessionService {
  private maintenance: MaintenanceService;
  private machines: MachineManagementService;
  private quickscan: QuickscanService;
  private application: ApplicationService;
  private msal: MsalService;
  private broadcast: MsalBroadcastService;
  private http: HttpService;
  private title: Title;

  public status: BehaviorSubject<SessionStatus>;
  public user: User | null;

  public role: BehaviorSubject<SessionRole | null>;
  public roles: CollectionSubject<SessionRole>;

  public groups: UserGroup[];
  public permissions: SessionPermission[];

  public customers: Customer[];
  public customer: BehaviorSubject<KlopCustomer | null>;

  public profiles: CollectionSubject<KlopProfile>;
  public profile: BehaviorSubject<KlopProfile | null>;

  public constructor() {
    this.maintenance = inject(MaintenanceService);
    this.quickscan = inject(QuickscanService);
    this.application = inject(ApplicationService);
    this.msal = inject(MsalService);
    this.broadcast = inject(MsalBroadcastService);
    this.http = inject(HttpService);
    this.title = inject(Title);
    this.machines = inject(MachineManagementService);

    this.status = new BehaviorSubject<SessionStatus>(SessionStatus.DEFAULT);
    this.user = null;

    this.role = new BehaviorSubject<SessionRole | null>(null);
    this.roles = new CollectionSubject();

    this.groups = [];
    this.permissions = [];

    this.customers = [];
    this.customer = new BehaviorSubject<KlopCustomer | null>(null);

    this.profiles = new CollectionSubject();
    this.profile = new BehaviorSubject<KlopProfile | null>(null);

    this.broadcast.msalSubject$
      .pipe(filter((message) => message.eventType == EventType.ACQUIRE_TOKEN_SUCCESS))
      .subscribe((message) => this.setToken(<AquireTokenSuccessPayload>message.payload));

    this.broadcast.inProgress$.pipe(filter((status) => status == "none")).subscribe(() => this.onAuthenticationEnd());
    this.status.subscribe((state) => this.onStatusChange(state));

    this.role.subscribe((role) => this.onRoleChange(role));
    this.roles.pipe(skip(1)).subscribe((roles) => this.onRolesChange(roles));

    this.profile.pipe(pairwise()).subscribe(([current, next]) => this.onProfileChange(current, next));
    this.profiles.subscribe((profiles) => this.onProfilesChange(profiles));
    this.customer.subscribe((customer) => this.onCustomerChange(customer));

    const router = inject(Router);
    this.status.subscribe(async (s) => {
      switch (s) {
        case SessionStatus.VALID: {
          const config = await firstValueFrom(this.quickscan.config.pipe(filter((i) => !!i)));
          router.navigate([config.dok.type == "POWERBI" ? "/powerbi" : "/quickscan"]);
          break;
        }
      }
    });
  }

  /**
   * check if session has permission
   * @param id
   * @returns
   */
  public hasPermission(id: string): boolean {
    if (Object.prototype.hasOwnProperty.call(SessionPermission, id)) {
      return this.permissions.includes(SessionPermission[id]);
    } else {
      throw new Error(` Could not find permission: ${id}`);
    }
  }

  /**
   * Set the correct JWT based on scope
   * @param payload
   */
  private setToken(payload: AquireTokenSuccessPayload): void {
    if (payload.scopes.filter((scope) => scope.includes("/user_impersonation")).length) {
      sessionStorage.setItem("smartlink-bo-token", payload.accessToken);
    } else {
      sessionStorage.setItem("accessToken", payload.accessToken);
    }
  }

  /**
   * Called after authentication is complete or has failed
   */
  private onAuthenticationEnd(): void {
    const account = this.msal.instance.getActiveAccount();
    if (account) {
      this.status.next(SessionStatus.IN_PROGRESS);
    } else {
      const accounts = this.msal.instance.getAllAccounts() ?? [];
      this.msal.instance.setActiveAccount(accounts.shift() ?? null);
      this.msal.acquireTokenSilent({ scopes: ENVIRONMENT.MSAL.scopes }).subscribe((res) => {
        if (res.account) {
          this.status.next(SessionStatus.IN_PROGRESS);
        } else {
          this.status.next(SessionStatus.INVALID);
        }
      });
    }
  }

  private getDokFromURL(): string {
    const dok = window.location.hostname.split(".").shift()?.toLowerCase();
    if (!dok) throw new Error("Unable to get DOK from URL");
    return dok;
  }

  /**
   * Event triggered when session status changes
   * @param state
   */
  private async onStatusChange(state: SessionStatus): Promise<void> {
    try {
      if (state == SessionStatus.IN_PROGRESS) {
        console.log("in progress");
        this.application.loading.next(true);
        const user = (this.user = await this.http.get<UserInfoResponse>(API_ROUTES.getUserInfo));
        this.groups = user.groups;

        const profile = await this.http.get<KlopProfile>(API_ROUTES.getActiveProfileWithCustomer);
        // this.onCustomerChange(profile.customer);
        // this.customer.next(profile.customer);
        this.profile.next(profile);
        // const customers = await this.http.get<Customer[]>(API_ROUTES.getCustomers);
        // const profiles = await this.http.get<Profile[]>(Misc.Translate(API_ROUTES.getProfiles, { userId: user.id }));
        // this.customers = customers;
        // this.profiles.next(profiles);
      } else if (state == SessionStatus.INVALID) {
        this.user = null;
        this.groups = [];
        this.roles.next([]);
        this.role.next(null);
      } else {
        this.application.loading.next(false);
      }
    } catch (err) {
      console.error(err);
      this.status.next(SessionStatus.UNAVAILABLE);
    }
  }

  /**
   * Fires when regions changes.
   * @param roles
   */
  private async onProfilesChange(profiles: KlopProfile[]): Promise<void> {
    try {
      console.warn("[SESSION] Profiles changed => ", profiles);
      if (this.user && profiles.length) {
        const profile = await this.http.get<KlopProfile>(API_ROUTES.getActiveProfile, { userId: this.user.id });
        if (profiles.find((_profile) => _profile.id == profile.id)) {
          this.profile.next(profile);
        } else {
          this.profile.next(profiles[0]);
        }
      }
    } catch (err) {
      console.error(err);
      this.status.next(SessionStatus.UNAVAILABLE);
    }
  }

  private async onCustomerChange(customer: KlopCustomer | null): Promise<void> {
    try {
      if (this.user && customer) {
        const config = await this.http.get<GetConfigResponse>(API_ROUTES.getConfig, { dok: this.getDokFromURL() });
        let dok = config.dok.name.toLowerCase();
        if (dok === "oodokoo") dok = "oooo";
        if (dok === "ndokno") dok = "ndok";
        const service = await this.http.get<HealthCheckResponse>(API_ROUTES.healthCheck, { dok });

        // const quickscans = await this.http.get(`${ENVIRONMENT.API}/getQuickscans?version=1&dok=ndok`);
        // console.log(quickscans);

        if (service.status == "ok") {
          this.title.setTitle(config.dok.name.toLowerCase());
          if (config.dok.machineManagement) {
            this.http
              .get<List<MachineManagementConfig>>(API_ROUTES.getMachineManagementConfig)
              .then((config) => {
                console.log("Machine management config =>", config);
                this.machines.initialize({
                  routes: {
                    getSourceTypes: API_ROUTES.getSourceTypes,
                    createVehicle: API_ROUTES.createVehicle,
                    getVehicles: API_ROUTES.getVehicles,
                    updateVehicle: API_ROUTES.updateVehicle,
                  },
                  config: config.items[0],
                });
              })
              .catch((e) => {
                console.log(e);
              });
          }
          this.maintenance.maintenances = config.maintenance ?? [];
          this.quickscan.account = { ...this.user, companyName: customer.customerName };
          this.quickscan.api = {
            routes: {
              createQuickscan: API_ROUTES.createQuickscan,
              getPowerBiReport: API_ROUTES.getMJOPReport,
              getQuickscans: API_ROUTES.getQuickscans,
              getVehicles: API_ROUTES.getVehicles,
              getDefaultVehicles: API_ROUTES.getDefaultVehicles,
              getSourceTypes: API_ROUTES.getSourceTypes,
            },
          };
          this.quickscan.config.next(config);

          const groups = this.user.groups
            .map((group) => group.displayName)
            .filter((name) =>
              config.dok.name.toLocaleLowerCase() == "oodokoo"
                ? name.includes("_wo2_")
                : name.includes(`_${config.dok.name.toLowerCase()}_`) &&
                  (ENVIRONMENT.TYPE != "PRD" ? name.includes(`_${ENVIRONMENT.TYPE}_`) : !(name.includes("_ACC_") || name.includes("_ACC_"))),
            );

          const roles = groups
            .map((group) => {
              if (group.includes("_manager")) return ROLES.MANAGER;
              if (group.includes("_user")) return ROLES.USER;
              return null;
            })
            .filter((i) => i != null);
          console.log("roles from oncustomerhcange => ", { roles, groups, userGroups: this.user.groups });
          this.roles.next(roles);
        } else {
          throw new Error("Services unavailable XDOK");
        }
      }
    } catch (err) {
      console.error(err);
      this.status.next(SessionStatus.UNAVAILABLE);
    }
  }

  /**
   * Fires when current profile changes
   * @param profile
   */
  private async onProfileChange(current: KlopProfile | null, next: KlopProfile | null): Promise<void> {
    try {
      console.warn("[SESSION] Profile changed => ", next);
      if (next && this.user && this.user.id) {
        if (current && current.id != next.id) {
          this.application.loading.next(true);
          await this.http.post<KlopProfile>(Misc.Translate(API_ROUTES.switchProfile, { userId: this.user.id }), { profileId: next.id });
          this.application.loading.next(false);
        }

        // const customer = this.customers.find((customer) => customer.id == next.customer_id);
        this.customer.next(next.customerProfile.customer);
      }
    } catch (err) {
      console.error("Unable to switch profiles => ", err);
      this.application.loading.next(false);
      this.status.next(SessionStatus.UNAVAILABLE);
    }
  }

  /**
   * Fires when roles changes.
   * @param roles
   */
  private onRolesChange(roles: SessionRole[]): void {
    try {
      if (roles.length) {
        const code = localStorage.getItem(STORAGE.ROLE);
        const role = code ? (roles.find((role) => role.code == code) ?? roles[0]) : roles[0];
        this.role.next(role);
      } else {
        throw new Error("Roles are empty.");
      }
    } catch (err) {
      console.error(err);
      this.status.next(SessionStatus.UNAVAILABLE);
    }
  }

  /**
   * Fires when active role changes
   * @param role
   */
  private onRoleChange(role: SessionRole | null): void {
    try {
      if (role) {
        localStorage.setItem(STORAGE.ROLE, role.code);
        this.permissions = this.getPermissions(role);
        this.status.next(SessionStatus.VALID);
      } else {
        localStorage.removeItem(STORAGE.ROLE);
      }
    } catch (err) {
      console.error(err);
      this.status.next(SessionStatus.UNAVAILABLE);
    }
  }

  /**
   * Get User permissions based on group
   * @param group
   */
  private getPermissions(role: SessionRole | null): SessionPermission[] {
    if (role) {
      return PERMISSIONS[role.code] ?? [];
    } else {
      return [];
    }
  }
}

export interface KlopProfile {
  id: string;
  uid: string;
  customer_profile_id: string;
  customer_id: string;
  label: string;
  description: string;
  active: boolean;
  customerProfile: CustomerProfile;
}

export interface CustomerProfile {
  id: string;
  profileId: string;
  date: string;
  label: string;
  description: string;
  active: boolean;
  status: string;
  securityGroups: SecurityGroup[];
  customer: KlopCustomer;
}

export interface SecurityGroup {
  displayName: string;
  id: string;
}
